import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useFundContext from '@/contexts/Fund/useFundContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import ActionIcon from '@/components/ui/ActionIcon/ActionIcon';
import Blade from '@/components/ui/Blade/Blade';
/*
import CustomMenu from "@/components/ui/Menu";
*/
import Transaction from '@/models/Transaction';
import TransactionAdditionalInfo, { transactionAdditionalInfoUrl } from '@/models/TransactionAdditionalInfo';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import payment from '@/assets/icons/payment.svg';
import donation from '@/assets/icons/donation.svg';
import support from '@/assets/icons/cell-support.svg';
import send from '@/assets/icons/send.svg';
import download from '@/assets/icons/download.svg';
import whatsapp from '@/assets/icons/whatsapp.svg';
import email from '@/assets/icons/email.svg';
/*
import menuDownload from "@/assets/icons/download-file.svg";
import menuSupport from "@/assets/icons/issue-support.svg";
*/
import styles from './AdditionalInfo.module.css';
import ReceiptEmailDialog from './ReceiptEmailDialog';
import { toReceiptToSend } from '@/models/out/ReceiptToSendDtoOut';
import DonorDonationsReportControl from '@/components/DonorDonationsReport/DonorDonationsReportControl';

interface AdditionalInfoProps {
  transaction: Transaction;
  handleXclick: () => void;
  exportTransaction: (transaction: Transaction) => void;
  issueSupport: (transaction: Transaction) => void;
  downloadReceipt: (receiptId: number, defaultReceipt?: boolean) => void;
  onContactNameClick: (contactName: string) => void;
}

const AdditionalInfo = ({
  transaction,
  handleXclick,
  /*
  exportTransaction,
  issueSupport,
  */
  downloadReceipt,
  onContactNameClick,
}: AdditionalInfoProps) => {
  const { t, i18n } = useTranslation();
  const { isCurrentUserAdmin } = useAuthContext();
  const { balance } = useFundContext();
  const [isSendReceiptOpen, setIsSendReceiptOpen] = useState(false);
  const sherutPlusIds = JSON.parse(import.meta.env.VITE_APP_SHERUT_PLUS_IDS);
  const {
    data: transactionInfo,
    isLoading: isInfoLoading,
    isError: isInfoError,
  } = useGetAuthApi<TransactionAdditionalInfo>(transactionAdditionalInfoUrl(transaction));
  
  const isAmountNegative = transaction.amount < 0;
  const amountColors = isAmountNegative ? 'text-(--font-primary) bg-(--surface-primary-lowest)' : 'text-(--font-tertiary) bg-(--surface-tertiary-low)';
  /*
  const menuItems = [
    {
      icon: menuDownload,
      text: t("exportTransactionDetails"),
      onClick: () => exportTransaction(transaction),
    },
    {
      icon: menuSupport,
      text: t("issueSupportCertificate"),
      onClick: () => issueSupport(transaction),
    },
  ];
  */

  let icon = undefined as string | undefined;

  switch (transaction.kind) {
    case 'תרומה':
      icon = donation;
      break;
    case 'תשלום ספקים':
    case 'תשלום ספקים (הוראת קבע)':
      icon = payment;
      break;
    case 'תמיכה':
    case 'מלגה':
      icon = support;
      break;
  }

  let kindText = `${transaction.kind} `;
  switch (transaction.kind) {
    case 'תרומה':
      kindText = t('donationFrom');
      break;
    case 'תשלום ספקים':
    case 'תשלום ספקים (הוראת קבע)':
      kindText = t('paymentTo');
      break;
    case 'תמיכה':
    case 'מלגה':
      kindText = t('supportTransferTo');
      break;
  }

  const transactionDetails = () => {
    switch (transaction.kind) {
      case 'תרומה':
        return (
          <section className={styles['transaction-details']}>
            <h3 className="body-md-semibold text-(--font-heading)">{t('donorDetails')}</h3>
            <div className="body-md-regular text-(--font-body-1)">
              <p>{transaction.contactName}</p>
              {transactionInfo?.contactDetails.phone && (
                <p>
                  {transactionInfo?.contactDetails.phone}{' '}
                  <a href={`tel:${transactionInfo?.contactDetails.phone}`}>
                    <img src={whatsapp} alt="" />
                  </a>
                </p>
              )}
              {transactionInfo?.contactDetails.email && (
                <p>
                  {transactionInfo?.contactDetails.email}{' '}
                  <a href={`mailto:${transactionInfo?.contactDetails.email}`}>
                    <img src={email} alt="" />
                  </a>
                </p>
              )}
              <p>
                {t('identificationCode')}: {transaction.contactId}
              </p>
            </div>
            {transaction.contactId ? (
              <div className="mt-3">
                <h3 className="body-md-semibold text-(--font-heading) mb-2">{t('donorDonationsReport.title')}</h3>
                <DonorDonationsReportControl
                  key={`${transaction.fundId}-${transaction.contactId}`}
                  yearsUrl={`funds/financial-data/${transaction.fundId}/donors/${transaction.contactId}/donations-report/years`}
                  reportUrlOf={(year) => `funds/financial-data/${transaction.fundId}/donors/${transaction.contactId}/donations-report?year=${year}`}
                  previewUrlOf={(year) => `funds/financial-data/${transaction.fundId}/donors/${transaction.contactId}/donations-report/preview?year=${year}`}
                  fileNameOf={(year) => `${t('donorDonationsReport.fileName')} ${transaction.contactName} ${year}`}
                />
              </div>
            ) : null}
          </section>
        );
      case 'תשלום ספקים':
      case 'תשלום ספקים (הוראת קבע)':
      case 'תמיכה':
      case 'מלגה':
        return (
          <>
            <section className={styles['transaction-details']}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('targetBankDetails')}</h3>
              <div className="body-md-regular text-(--font-body-2)">
                <p>{transactionInfo?.bankAccountDetails.accountOwnerName}</p>
                {transactionInfo?.bankAccountDetails.accountNumber && transactionInfo?.bankAccountDetails.branchNumber && (
                  <p>
                    {transactionInfo?.bankAccountDetails.accountNumber}/{transactionInfo?.bankAccountDetails.branchNumber}
                  </p>
                )}
                <p>
                  {transactionInfo?.bankAccountDetails.bankName} {transactionInfo?.bankAccountDetails.bankNumber}
                </p>
              </div>
            </section>
            <section className={styles['transaction-details']}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('contactDetails')}</h3>
              <div className="body-md-regular text-(--font-body-2)">
                <p>
                  {transactionInfo?.contactDetails.firstName} {transactionInfo?.contactDetails.lastName}
                </p>
                {transactionInfo?.contactDetails.phone && (
                  <p>
                    {transactionInfo?.contactDetails.phone}{' '}
                    <a href={`tel:${transactionInfo?.contactDetails.phone}`}>
                      <img src={whatsapp} alt="" />
                    </a>
                  </p>
                )}
                {transactionInfo?.contactDetails.email && (
                  <p>
                    {transactionInfo?.contactDetails.email}{' '}
                    <a href={`mailto:${transactionInfo?.contactDetails.email}`}>
                      <img src={email} alt="" />
                    </a>
                  </p>
                )}
              </div>
            </section>
          </>
        );
    }
  };

  const receiptSection = () => {
    return (
      <section>
        <h3 className="body-md-semibold text-(--font-heading)">{t('receipt')}</h3>
        <div className="flex justify-between items-center">
          <p className={`${transaction.receiptId ? 'body-md-link text-(--font-link)' : 'body-md-regular text-(--font-body-1)'}`}>
            {transaction.receiptId ? `${t('receiptNumber')} ${transaction.receiptId}` : t('noReceipt')}
          </p>
          {transaction.receiptId ? (
            <div className="flex gap-[14px]">
              <ActionIcon icon={send} onClick={() => setIsSendReceiptOpen(true)} />
              <ActionIcon icon={download} onClick={() => downloadReceipt(transaction.receiptId, transaction.defaultReceipt)} />
            </div>
          ) : null}
        </div>
      </section>
    );
  };

  /*
   * HAT-1170 — סעיף ההמרה, לא היה קיים במגירה בכלל.
   *
   * בדסקטופ המידע הזה (סכום המקור, שער ההמרה, הסכום בשקלים) ישב
   * אך ורק בתוך tooltip שנפתח בריחוף מעל הסכום בטבלה. במסך מגע
   * אין ריחוף — וזו הסיבה שכרטיס התנועה הציג את זה כטקסט קבוע
   * בשורת המטא. כשהשורה ההיא התקצרה כדי שהכרטיס לא ייראה עמוס,
   * המידע חייב מקום חדש שלא תלוי בריחוף — כאן, במגירה, לצד הקבלה.
   * אותם מפתחות תרגום בדיוק כמו ה-tooltip בטבלה, כדי שהניסוח יהיה
   * זהה בשני המקומות.
   */
  const conversionSection = () => {
    if (!transaction.ilsAmount || !transaction.exchangeRate || balance?.currency !== 'ILS') return null;
    return (
      <section>
        <h3 className="body-md-semibold text-(--font-heading)">{t('totalAmount')}</h3>
        <p className="body-md-regular text-(--font-body-1)">
          {t('conversionAmountNote1')}{' '}
          <span className="font-bold">
            {transaction.amount.toLocaleString(i18n.language, { style: 'currency', currency: transaction.currency })}
          </span>
          <br />
          {t('conversionAmountNote2')} {transaction.exchangeRate.toFixed(2)}
          <br />
          {t('conversionAmountNote3')}{' '}
          <span className="font-bold">{transaction.ilsAmount.toLocaleString(i18n.language, { style: 'currency', currency: 'ILS' })}</span>
        </p>
      </section>
    );
  };

  const notesSection = () => {
    return (
      <section>
        <h3 className="body-md-semibold text-(--font-heading)">{t('notes')}</h3>
        {transaction.monthlyCommission &&
          transaction.monthlyCommission.map((commission, index) => (
            <p className="body-md-regular text-(--font-body-1)" key={index}>
              {commission.category}, {t('count')}: {commission.count}, {t('amount')}: {commission.totalSum}
            </p>
          ))}
        {isCurrentUserAdmin() && (
          <p>
            {t('transactionId')}: {transaction.transactionId}
            <br />
            {t('transactionDetailId')}: {transaction.transactionDetailId}
          </p>
        )}
      </section>
    );
  };

  const paymentMethod = () => {
    return (
      <section>
        <h3 className="body-md-semibold text-(--font-heading)">{t('paymentMethodIdentifier')}</h3>
        {transactionInfo?.bankAccountDetails.accountNumber &&
          transactionInfo?.bankAccountDetails.branchNumber &&
          transactionInfo?.bankAccountDetails.bankNumber && (
            <div className="body-md-regular text-(--font-body-2)">
              <p>{t('sourceBankDetails')}:</p>
              <p>{transactionInfo?.bankAccountDetails.accountOwnerName}</p>
              <p>
                {transactionInfo?.bankAccountDetails.accountNumber}/{transactionInfo?.bankAccountDetails.branchNumber}
              </p>
              <p>
                {transactionInfo?.bankAccountDetails.bankName} {transactionInfo?.bankAccountDetails.bankNumber}
              </p>
            </div>
          )}

        {transaction.paymentMethod.includes('credit') && (
          <div className="body-md-regular text-(--font-body-2)">
            {t('last4Digits')}: {transactionInfo?.last4Digits}
          </div>
        )}
      </section>
    );
  };

  const sections: ReactNode[] = [];
  if (
    !isInfoLoading &&
    !isInfoError &&
    !sherutPlusIds.includes(transaction.contactId) &&
    (transaction.kind == 'תרומה' || transaction.kind.includes('תשלום ספקים') || transaction.kind == 'תמיכה' || transaction.kind == 'מלגה')
  ) {
    sections.push(transactionDetails());
  }
  if (transaction.kind === 'תרומה' && (transaction.paymentMethod === 'creditCard' || transaction.paymentMethod === 'directDebit')) {
    sections.push(paymentMethod());
  }
  if (!sherutPlusIds.includes(transaction.contactId) && (transaction.kind === 'תרומה' || transaction.kind === 'פיקדון' || transaction.kind === 'הלוואה')) {
    sections.push(receiptSection());
  }
  sections.push(conversionSection());
  sections.push(notesSection());

  const headerTop = (
    <>
      <span className="body-lr-regular">
        {kindText}
        {' - '}
        <span className="body-lr-link" onClick={() => onContactNameClick(transaction.contactName)}>
          {transaction.contactName}
        </span>
      </span>
      <span className="flex gap-[10px] items-center">
        {/*
        <CustomMenu items={menuItems} variant="white" />
        <span className={styles.divider}></span>
        */}
      </span>
    </>
  );

  const headerBottom = (
    <>
      <span className={`${styles.amount} body-sm-medium ${amountColors}`}>
        <img src={icon} alt="" />
        {transaction.amount.toLocaleString(i18n.language, {
          style: 'currency',
          currency: transaction.currency || 'ILS',
        })}
      </span>
      <span className="body-sm-regular">
        {Intl.DateTimeFormat('he-IL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(new Date(transaction.date))}
      </span>
    </>
  );

  return (
    <Blade handleXclick={handleXclick} headerTop={headerTop} headerBottom={headerBottom} width="456px">
      <Tabs defaultValue="additionalInfo" style={{ direction: 'inherit' }}>
        <TabsList>
          <TabsTrigger value="additionalInfo">{t('additionalInfo')}</TabsTrigger>
        </TabsList>
        <TabsContent value="additionalInfo" className={`${styles.content} custom-scrollbar`}>
          {sections.map((section, index, arr) => (
            <>
              {section}
              {index !== arr.length - 1 && <div className={styles.line} />}
            </>
          ))}
          <ReceiptEmailDialog
            receipts={transaction.receiptId ? [toReceiptToSend(transaction)] : []}
            defaultEmail={transactionInfo?.contactDetails.email}
            isSendReceiptOpen={isSendReceiptOpen}
            setIsSendReceiptOpen={setIsSendReceiptOpen}
          />
        </TabsContent>
      </Tabs>
    </Blade>
  );
};

export default AdditionalInfo;
